import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Breadcrumb, Card, Col, Row } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import { useSearchParams } from 'react-router-dom';
import { Private_Routes } from '../../constants';
import ErrorMessage from '../../common/filter-table/ErrorMessage';
import Loading from '../../common/filter-table/Loading';
import Pager from '../../common/filter-table/Pager';
import PageSizeSelector from '../../common/filter-table/PageSizeSelector';
import SearchInput from '../../common/filter-table/SearchInput';
import { renderErrorMessage } from '../../utils/renderErrorMessage';
import { AuditLogsListFilters, fetchAuditLogsList } from './api/auditLogs.api';
import { AuditLogsFilters, AuditLogsFiltersValues } from './components/AuditLogsFilters';
import { AuditLogsTable } from './components/AuditLogsTable';
import { RequireAuditModuleAccess } from './RequireAuditModuleAccess';

function emptyFilters(): AuditLogsFiltersValues {
  return {
    entityName: '',
    primaryKey: '',
    startDate: '',
    endDate: '',
    userId: '',
    employeeId: '',
    operation: '',
    correlationId: '',
  };
}

function filtersFromSearchParams(sp: URLSearchParams): AuditLogsFiltersValues {
  return {
    entityName: sp.get('entityName')?.trim() ?? '',
    primaryKey: sp.get('primaryKey')?.trim() ?? '',
    startDate: sp.get('startDate')?.trim() ?? '',
    endDate: sp.get('endDate')?.trim() ?? '',
    userId: sp.get('userId')?.trim() ?? '',
    employeeId: sp.get('employeeId')?.trim() ?? '',
    operation: sp.get('operation')?.trim() ?? '',
    correlationId: sp.get('correlationId')?.trim() ?? '',
  };
}

function validateEntityPair(sp: URLSearchParams): string | null {
  const entityName = sp.get('entityName')?.trim() ?? '';
  const pkRaw = sp.get('primaryKey')?.trim() ?? '';
  const pkNum = pkRaw === '' ? NaN : Number(pkRaw);
  return null;
}

function buildListFilters(
  sp: URLSearchParams,
  pageIndex: number,
  pageSize: number,
  searchTerm: string,
): AuditLogsListFilters | null {
  const entityName = sp.get('entityName')?.trim() ?? '';
  const pkRaw = sp.get('primaryKey')?.trim() ?? '';
  const pkNum = pkRaw === '' ? NaN : Number(pkRaw);

  const uidRaw = sp.get('userId')?.trim() ?? '';
  const userId = uidRaw === '' ? undefined : Number(uidRaw);

  const empRaw = sp.get('employeeId')?.trim() ?? '';
  const employeeId = empRaw === '' ? undefined : Number(empRaw);

  const opRaw = sp.get('operation')?.trim() ?? '';
  const operation = opRaw === '' ? undefined : Number(opRaw);

  return {
    entityName: entityName || undefined,
    startDate: sp.get('startDate')?.trim() || undefined,
    endDate: sp.get('endDate')?.trim() || undefined,
    userId: userId !== undefined && !Number.isNaN(userId) && userId > 0 ? userId : undefined,
    employeeId:
      employeeId !== undefined && !Number.isNaN(employeeId) && employeeId > 0
        ? employeeId
        : undefined,
    operation: operation !== undefined && !Number.isNaN(operation) ? operation : undefined,
    correlationId: sp.get('correlationId')?.trim() || undefined,
    offset: pageIndex * pageSize,
    top: pageSize,
    orderBy: 'date',
    ascending: false,
    searchTerm,
  };
}

function AuditLogsIndexInner() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState<AuditLogsFiltersValues>(() =>
    filtersFromSearchParams(searchParams),
  );

  const [isLoading, setIsLoading] = useState(false);
  const [hasErrors, setHasErrors] = useState(false);
  const [error, setError] = useState({ title: '', message: '' });
  const [data, setData] = useState<import('./models/auditLog.model').AuditLogDetailsDto[]>([]);
  const [filteredRecords, setFilteredRecords] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  const [searchTerm, setSearchTerm] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  const [applyValidation, setApplyValidation] = useState<string | undefined>();

  useEffect(() => {
    setFilters(filtersFromSearchParams(searchParams));
  }, [searchParams]);

  const urlFilterError = useMemo(() => validateEntityPair(searchParams), [searchParams]);

  const load = useCallback(async () => {
    const urlErr = validateEntityPair(searchParams);
    if (urlErr) {
      setData([]);
      setFilteredRecords(0);
      setTotalRecords(0);
      setHasErrors(false);
      setError({ title: '', message: '' });
      return;
    }

    const built = buildListFilters(searchParams, pageIndex, pageSize, searchTerm);
    if (!built) {
      return;
    }

    setIsLoading(true);
    setHasErrors(false);
    setError({ title: '', message: '' });
    try {
      const res = await fetchAuditLogsList(built);
      setData(res.data ?? []);
      setFilteredRecords(res.filteredRecordsCount ?? 0);
      setTotalRecords(res.totalRecordsCount ?? 0);
    } catch (e: unknown) {
      const ax = e as { response?: { status?: number; data?: unknown }; message?: string };
      const serverMsg =
        typeof ax.response?.data === 'string'
          ? ax.response.data
          : (ax.response?.data as { message?: string })?.message;
      const err = e as { message?: string };
      const msg = renderErrorMessage(ax.response?.status, serverMsg || err.message);
      setHasErrors(true);
      setError(msg);
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }, [searchParams, pageIndex, pageSize, searchTerm]);

  useEffect(() => {
    void load();
  }, [load]);

  const applyFilters = () => {
    setApplyValidation(undefined);
    const entityName = filters.entityName.trim();

    const next = new URLSearchParams();
    if (entityName) {
      next.set('entityName', entityName);
    }
    if (filters.startDate) {
      next.set('startDate', filters.startDate);
    }
    if (filters.endDate) {
      next.set('endDate', filters.endDate);
    }
    if (filters.userId && filters.userId.trim()) {
      next.set('userId', filters.userId.trim());
    }
    if (filters.employeeId && filters.employeeId.trim()) {
      next.set('employeeId', filters.employeeId.trim());
    }
    if (filters.operation !== '') {
      next.set('operation', filters.operation);
    }
    if (filters.correlationId.trim()) {
      next.set('correlationId', filters.correlationId.trim());
    }

    setPageIndex(0);
    setSearchParams(next);
  };

  const clearFilters = () => {
    setApplyValidation(undefined);
    setFilters(emptyFilters());
    setPageIndex(0);
    setSearchParams({});
  };

  const listSearchSuffix = searchParams.toString() ? `?${searchParams.toString()}` : '';

  const showTable = !urlFilterError;

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">
          <Helmet>
            <title>Auditoría</title>
          </Helmet>

          <Row className="py-0">
            <Col sm={12} className="p-3 py-0">
              <Breadcrumb>
                <Breadcrumb.Item href={Private_Routes.HOME}>Inicio</Breadcrumb.Item>
                <Breadcrumb.Item active>Auditoría</Breadcrumb.Item>
              </Breadcrumb>

              <Card>
                <Card.Header>
                  <h5 className="mb-0">Historial de auditoría</h5>
                  <p className="text-muted small mb-0">
                    Filtre por entidad, o por fechas, usuario, operación o correlación. Sin filtros
                    se listan todos los registros paginados.
                  </p>
                </Card.Header>
                <Card.Body>
                  <AuditLogsFilters
                    values={filters}
                    onChange={setFilters}
                    onApply={applyFilters}
                    onClear={clearFilters}
                    disabled={isLoading}
                    validationMessage={applyValidation}
                  />

                  {urlFilterError && (
                    <div className="alert alert-warning mb-3 py-2" role="alert">
                      {urlFilterError} Corrija la dirección o use «Limpiar filtros».
                    </div>
                  )}

                  {showTable && (
                    <>
                      <div className="row m-0 mb-3">
                        <div className="col-sm-6 col">
                          <PageSizeSelector
                            initialPageSize={pageSize}
                            handleChange={(e: React.ChangeEvent) => {
                              setPageIndex(0);
                              setPageSize(parseInt((e.target as HTMLSelectElement).value, 10));
                            }}
                          />
                        </div>
                        <div className="col-sm-6 col">
                          <div className="d-flex justify-content-sm-end">
                            <div className="search-box ms-2 gridjs-search">
                              <SearchInput
                                value={searchTerm}
                                handleChange={(e: React.ChangeEvent) => {
                                  setPageIndex(0);
                                  setSearchTerm((e.target as HTMLInputElement).value);
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      {isLoading ? (
                        <Loading hasSkeleton={true} skeletonType="table" />
                      ) : hasErrors ? (
                        <ErrorMessage errorDescription={error.message} title={error.title} />
                      ) : (
                        <AuditLogsTable data={data} listSearchSuffix={listSearchSuffix} />
                      )}

                      <Pager
                        visible={showTable && !isLoading && !hasErrors}
                        handleChange={setPageIndex}
                        currentPageIndex={pageIndex}
                        pageSize={pageSize}
                        filteredRecords={filteredRecords}
                        totalRecords={totalRecords}
                      />
                    </>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
}

export default function AuditLogsIndexPage() {
  return (
    <RequireAuditModuleAccess>
      <AuditLogsIndexInner />
    </RequireAuditModuleAccess>
  );
}
